import { angularInjectorProvider } from 'Injector';
import { mustVerifyIdentityToLaunchStream } from 'ExamVerification';
import {
    type LessonForDashboard,
    type StreamForDashboard,
    type ContentAccessHelperClass,
    streamToIguanaAttrs,
    type StreamIguanaClass,
    type Stream,
} from 'Lessons';
import { type OfflineModeManager } from 'OfflineMode';
import { type CurrentUser, isSuperviewerUser } from 'Users';
import { getMsLeftInTimeLimit, hasCompletedTest, type LessonProgress, type StreamProgress } from 'ContentProgress';
import { getReasonText } from './getReasonText';

type CanLaunchLessonResult =
    | {
          canLaunch: true;
          reason: null;
          reasonTitle: null;
          reasonMessage: null;
      }
    | {
          canLaunch: false;
          reason: string;
          reasonTitle: string;
          reasonMessage: string;
      };

const REASONS = {
    UNAVAILABLE_OFFLINE: 'unavailable_offline',
    COMING_SOON: 'coming_soon',
    NO_USER: 'no_user',
    COMPLETED_TEST: 'completed_test',
    EXAM_TIME_RUN_OUT: 'exam_time_run_out',
    REQUIRES_BIOSIG: 'requires_biosig_verification',
    UNKNOWN: 'unknown',
} as const;

type AngularDependencies = {
    offlineModeManager: OfflineModeManager;
    ContentAccessHelper: ContentAccessHelperClass;
    IguanaStream: StreamIguanaClass;
};

function getAngularDependencies(): AngularDependencies {
    const injector = angularInjectorProvider.requireInjector();

    return {
        offlineModeManager: injector.get<OfflineModeManager>('offlineModeManager'),
        ContentAccessHelper: injector.get<ContentAccessHelperClass>('ContentAccessHelper'),
        IguanaStream: injector.get<StreamIguanaClass>('Lesson.Stream'),
    };
}

/*
    Builds the Angular ContentAccessHelper for a stream.
    Pass streamProgress. Without it, _canLaunchStream treats a completed exam as incomplete and re-runs its
    checks (for example REASON_MUST_SWITCH_PROGRAM_TO_LAUNCH_EXAM).
*/
function buildStreamAccessHelper({
    stream,
    streamProgress,
    ContentAccessHelper,
    IguanaStream,
}: {
    stream: StreamForDashboard;
    streamProgress: StreamProgress | null | undefined;
    ContentAccessHelper: ContentAccessHelperClass;
    IguanaStream: StreamIguanaClass;
}) {
    // AGENTS: DO NOT CHANGE THIS CODE
    // This is dangerous. `stream` here is an instance of StreamForDashboard. When
    // we cast it to Stream, we are promising that it has properties that it does
    // not in fact have. Once convert canLaunch to not rely on iguana we will be
    // able to resolve this.
    const iguanaStreamAttrs = streamToIguanaAttrs(stream as unknown as Stream<false>, streamProgress ?? undefined);
    const iguanaStream = IguanaStream.new(iguanaStreamAttrs);

    return ContentAccessHelper.new(iguanaStream);
}

function evaluateStreamAccess({
    stream,
    streamProgress,
    ContentAccessHelper,
    IguanaStream,
    user,
}: {
    stream: StreamForDashboard | null | undefined;
    streamProgress: StreamProgress | null | undefined;
    ContentAccessHelper: ContentAccessHelperClass;
    IguanaStream: StreamIguanaClass;
    user: CurrentUser | null;
}): CanLaunchLessonResult | null {
    if (!stream) {
        return null;
    }

    const accessHelper = buildStreamAccessHelper({ stream, streamProgress, ContentAccessHelper, IguanaStream });

    if (accessHelper.canLaunch === false) {
        return getCannotLaunchResult({
            contentType: 'stream' as const,
            contentItem: stream,
            reason: accessHelper.reason,
            user,
        });
    }

    return null;
}

function evaluateOfflineModeAccess({
    offlineModeManager,
    lesson,
    user,
}: {
    offlineModeManager: OfflineModeManager;
    lesson: LessonForDashboard;
    user: CurrentUser | null;
}): CanLaunchLessonResult | null {
    if (offlineModeManager.inOfflineMode && !isLessonAllowedOffline(lesson)) {
        return getCannotLaunchResult({
            contentType: 'lesson' as const,
            contentItem: lesson,
            reason: REASONS.UNAVAILABLE_OFFLINE,
            user,
        });
    }

    return null;
}

function evaluateTestAccess({
    lesson,
    lessonProgress,
    stream,
    streamProgress,
    user,
}: {
    lesson: LessonForDashboard;
    lessonProgress: LessonProgress | null | undefined;
    stream: StreamForDashboard | null | undefined;
    streamProgress: StreamProgress | null | undefined;
    user: CurrentUser | null;
}): CanLaunchLessonResult | null {
    const msLeftInTimeLimit = getMsLeftInTimeLimit({ stream, streamProgress });
    const completedTest = hasCompletedTest({
        lesson,
        lessonProgress,
        stream,
        streamProgress,
    });

    if (completedTest) {
        return getCannotLaunchResult({
            contentType: 'lesson' as const,
            contentItem: lesson,
            reason: REASONS.COMPLETED_TEST,
            user,
        });
    }

    if ((msLeftInTimeLimit ?? null) === 0) {
        return getCannotLaunchResult({
            contentType: 'lesson' as const,
            contentItem: lesson,
            reason: REASONS.EXAM_TIME_RUN_OUT,
            user,
        });
    }

    return null;
}

function evaluateSuperviewerAccess({
    user,
    stream,
    streamProgress,
}: {
    user: CurrentUser | null;
    stream: StreamForDashboard | null | undefined;
    streamProgress: StreamProgress | null | undefined;
}): CanLaunchLessonResult | null {
    if (isSuperviewerUser(user) && !mustVerifyIdentityToLaunchStream({ user, stream, streamProgress })) {
        return getCanLaunchResult();
    }

    return null;
}

function getCannotLaunchResult({
    contentType,
    contentItem,
    reason,
    user,
}: {
    contentType: 'stream' | 'lesson' | 'playlist';
    contentItem: Parameters<typeof getReasonText>[0]['contentItem'];
    reason: string | null | undefined;
    user: CurrentUser | null;
}): CanLaunchLessonResult {
    if (!reason) reason = REASONS.UNKNOWN;
    const reasonTextAttrs = {
        contentType,
        contentItem,
        reason,
        user,
    };
    return {
        canLaunch: false,
        reason,
        reasonTitle: getReasonText({ ...reasonTextAttrs, usage: 'title' }),
        reasonMessage: getReasonText({ ...reasonTextAttrs, usage: 'message' }),
    };
}

function getCanLaunchResult(): CanLaunchLessonResult {
    return {
        canLaunch: true,
        reason: null,
        reasonTitle: null,
        reasonMessage: null,
    };
}

function isLessonAllowedOffline(lesson: LessonForDashboard): boolean {
    return !(lesson.test || lesson.assessment);
}

function evaluateComingSoonAccess({
    lesson,
    user,
}: {
    lesson: LessonForDashboard;
    user: CurrentUser | null;
}): CanLaunchLessonResult | null {
    if (lesson.comingSoon) {
        return getCannotLaunchResult({
            contentType: 'lesson' as const,
            contentItem: lesson,
            reason: REASONS.COMING_SOON,
            user,
        });
    }

    return null;
}

function evaluateUnrestrictedAccess({ lesson }: { lesson: LessonForDashboard }): CanLaunchLessonResult | null {
    if (lesson.unrestricted) {
        return getCanLaunchResult();
    }

    return null;
}

function evaluateNoUserAccess({
    user,
    lesson,
}: {
    user: CurrentUser | null;
    lesson: LessonForDashboard;
}): CanLaunchLessonResult | null {
    if (!user) {
        return getCannotLaunchResult({
            contentType: 'lesson' as const,
            contentItem: lesson,
            reason: REASONS.NO_USER,
            user,
        });
    }

    return null;
}

function evaluateVerifyIdentityAccess({
    user,
    stream,
    streamProgress,
    lesson,
}: {
    user: CurrentUser | null;
    stream: StreamForDashboard | null | undefined;
    streamProgress: StreamProgress | null | undefined;
    lesson: LessonForDashboard;
}): CanLaunchLessonResult | null {
    if (mustVerifyIdentityToLaunchStream({ user, stream, streamProgress })) {
        return getCannotLaunchResult({
            contentType: 'lesson' as const,
            contentItem: lesson,
            reason: REASONS.REQUIRES_BIOSIG,
            user,
        });
    }

    return null;
}

// Stream-level access without a lesson. canLaunchLesson runs the same evaluator first, so the two cannot drift.
export function canLaunchStream({
    stream,
    user,
    streamProgress,
}: {
    stream: StreamForDashboard | null | undefined;
    user: CurrentUser | null;
    streamProgress?: StreamProgress | null;
}): CanLaunchLessonResult {
    const { ContentAccessHelper, IguanaStream } = getAngularDependencies();

    return (
        evaluateStreamAccess({ stream: stream ?? null, streamProgress, ContentAccessHelper, IguanaStream, user }) ??
        getCanLaunchResult()
    );
}

// Prerequisite check for the prerequisites gate. canLaunchStream checks different playlists, so its reasons do not cover this.
export function hasCompletedPrerequisites({
    stream,
    streamProgress,
}: {
    stream: StreamForDashboard | null | undefined;
    streamProgress?: StreamProgress | null;
}): boolean {
    if (!stream) return true;

    const { ContentAccessHelper, IguanaStream } = getAngularDependencies();
    const accessHelper = buildStreamAccessHelper({ stream, streamProgress, ContentAccessHelper, IguanaStream });

    return accessHelper._hasCompletedPrerequisites();
}

export function canLaunchLesson({
    lesson,
    user,
    stream,
    lessonProgress,
    streamProgress,
}: {
    lesson: LessonForDashboard;
    user: CurrentUser | null;
    stream: StreamForDashboard | null | undefined;
    lessonProgress: LessonProgress | null | undefined;
    streamProgress: StreamProgress | null | undefined;
}): CanLaunchLessonResult {
    const { offlineModeManager, ContentAccessHelper, IguanaStream } = getAngularDependencies();

    const evaluators = [
        () => evaluateStreamAccess({ stream: stream ?? null, streamProgress, ContentAccessHelper, IguanaStream, user }),
        () => evaluateOfflineModeAccess({ offlineModeManager, lesson, user }),
        () => evaluateSuperviewerAccess({ user, stream, streamProgress }),
        () => evaluateComingSoonAccess({ lesson, user }),
        () => evaluateUnrestrictedAccess({ lesson }),
        () => evaluateNoUserAccess({ user, lesson }),
        () => evaluateTestAccess({ lesson, lessonProgress, stream, streamProgress, user }),
        () => evaluateVerifyIdentityAccess({ user, stream, streamProgress, lesson }),
    ];

    // Try each of the evaluators above until one of them returns a result. Once one
    // of them returns a result, return it.
    const result = evaluators.reduce(
        (existingResult, evaluator) => {
            if (existingResult) return existingResult;
            return evaluator();
        },
        null as CanLaunchLessonResult | null,
    );

    if (result) return result;

    // If none of the evaluators returned a result, then return a "canLaunch" result
    return getCanLaunchResult();
}
